import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { AjaxService } from './ajax.service';
import { AuthStateService } from './auth-state.service';
import { tap, map, catchError } from 'rxjs/operators';
import { PENDING_WORK, CUSTOMERS_OF_WORKER, COMPANIES_OF_WORKER} from './account-api.constants';

/**
 * @description
 * Interface to define the structure of a company object,
 * extracting only the key pieces of information you need from the API response.
 */
export interface Company {
    id: number;
    name: string;
}

export interface Customer {
    id: number;
    name: string;
    businessId: string;
}


@Injectable({
    providedIn: 'root'
})
export class ViewScopeService {
    private _companiesSubject = new BehaviorSubject<Company[]>([]);
    public companies$ = this._companiesSubject.asObservable();

    private _selectedCompanySubject = new BehaviorSubject<Company | null>(null);
    public selectedCompany$ = this._selectedCompanySubject.asObservable();

    private _customersSubject = new BehaviorSubject<Customer[]>([]);
    public customers$ = this._customersSubject.asObservable();

    private _selectedCustomerSubject = new BehaviorSubject<Customer | null>(null);
    public selectedCustomer$ = this._selectedCustomerSubject.asObservable();

    constructor(
        private ajaxService: AjaxService,
        private authStateService: AuthStateService
    ) {
        console.log('ViewScopeService has been initialized.');
    }

    /**
     * @description
     * Fetches the list of companies from the API, processes the data,
     * and updates the internal state of the service.
     * @returns An Observable of the list of companies.
     */
    public getCompanies(): Observable<Company[]> {
        const reqId = this.getCurrentOpertaingUserId().toString();
        if (reqId === '0') return of([]);
        const apiUrl = this.ajaxService.getBaseOfApi() + COMPANIES_OF_WORKER + reqId;
        

        return this.ajaxService.getData(apiUrl).pipe(
            map((apiResponse: any) => {
                const arr = apiResponse as any[];
                return arr.map(item => ({
                    id: item.CompanyRecID,
                    name: item.Company ?? item.Compamy
                })) as Company[];
            }),
            tap(mappedCompanies => {
                this._companiesSubject.next(mappedCompanies);
                if (mappedCompanies.length > 0) {
                    this._selectedCompanySubject.next(mappedCompanies[0]);
                }
            })
        );

    }

    public getCustomers(reqForOtherId? : number): Observable<Customer[]> {
        
        const operId =this.getCurrentOpertaingUserId().toString(); 
        const reqId = reqForOtherId ? reqForOtherId : operId;
        const compId = this.getSelectedCompanyId();
        
        if (reqId === '0' || compId == null || operId === '0') return of([]);
        const apiUrl = this.ajaxService.getBaseOfApi() + CUSTOMERS_OF_WORKER + compId + "/" + reqId + "/" + operId;


        return this.ajaxService.getData(apiUrl).pipe(
            map((apiResponse: any) => {
                const arr = apiResponse as any[];
                return arr.map(item => ({
                    id: item.CustomerSupplierRecID,
                    name: item.CustomerName,
                    businessId: item.Business_ID,
                    relId : item.UserCustomerRecID
                })) as Customer[];
            }),
            tap(customers => {
                this._customersSubject.next(customers);
                if (customers.length === 1) {
                    this._selectedCustomerSubject.next(customers[0]);
                }
            })
        );
    }


    /**
     * @description
     * Set the currently selected company.
     */
    public setSelectedCompany(company: Company): void {
        this._selectedCompanySubject.next(company);
    }

    /**
     * @description
     * Set the currently selected customer. SelectAccountComponent is the sole owner of
     * customer-selection logic (restoring a saved pick, validating it, defaulting to the
     * first result) and must call this whenever it resolves a selection - getCustomers()
     * only auto-populates this subject when there's exactly one customer in the result.
     */
    public setSelectedCustomer(customer: Customer): void {
        this._selectedCustomerSubject.next(customer);
    }


    public getSelectedCompanyId(): number | null {
        const selectedCompanyString = localStorage.getItem('selectedCompany');

        // Step 1 & 2: Get the item and check if it exists.
        if (selectedCompanyString) {
            try {
                // Step 3: Parse the JSON string into an object.
                const parsedCompany = JSON.parse(selectedCompanyString);

                // Check if the id property exists before returning.
                if (parsedCompany && typeof parsedCompany.id === 'number') {
                    return parsedCompany.id;
                }
            } catch (e) {
                // Log an error if the data is not valid JSON.
                console.error("Could not parse 'selectedCompany' from localStorage:", e);
            }
        }

        // Return null if the item doesn't exist or is invalid.
        return null;
    }


    public getCurrentCustomer() {
        const selectedCustomerString = localStorage.getItem('selectedCustomer');

        if (selectedCustomerString) {
            return JSON.parse(selectedCustomerString).id;
        }
        else {
            console.log('No customer data found in localStorage.');
        }
    }



    public getCurrentCustomerName() {
        const selectedCustomerString = localStorage.getItem('selectedCustomer');

        if (selectedCustomerString) {
            return JSON.parse(selectedCustomerString).name;
        }
        else {
            console.log('No customer data found in localStorage.');
        }
    }


    public getCurrentOpertaingUserId(): number {
        // Backed by AuthStateService's in-memory state (populated from the session cookie via
        // GET /api/login/me at app bootstrap) rather than localStorage - every one of this
        // method's ~113 call sites keeps working unchanged.
        return this.authStateService.getCurrentUserId();
    }

    public getPendingWorkForCustomer(currentOnly: boolean) {
        if(this.getCurrentOpertaingUserId() == 0) return of([]);
        const requestingID = currentOnly ? this.getCurrentCustomer() : -1;
        const apiUrl = this.ajaxService.getBaseOfApi() + PENDING_WORK + requestingID + '/'  + this.getSelectedCompanyId() + '/' + this.getCurrentOpertaingUserId();

        return this.ajaxService.getData(apiUrl).pipe(
            map((response: any) => {
                const table = response?.Table ?? [];
                return table.map((item: any) => ({
                    CustomerRecID: item.CustomerRecID,
                    NumberOfJournalRows: item.NumberOfJournalRows,
                    NumberOfFiles: item.NumberOfFiles
                }));
            }),
            catchError(error => {
                console.error('Error fetching pending work:', error);
                return of([]);
            })
        );
    }
}
